/**
 * `installKit()` — the UI kit's boot step (architecture §2.2 "tokens … fonts"): pigments on `<html>`, ornaments, the
 * glyph and emblem sprites, press tracking, the material bake, the art manifest and the fonts (≤ 1.5 s), then the
 * string-measure pass (`--label-w-ristalishche`, `--tab-lines`). Idempotent; F1 calls it once at boot, screens may
 * call it again safely. Owner: UI/Visual (F3).
 */
import '../styles/index';
import { applyTokens } from '../tokens';
import { applyOrnaments } from '../ornaments';
import { bakeMaterials, type BakeResult } from '../textures';
import { ensureShellSprite } from '../icons/shell.svg';
import { ensureEmblemSprite } from '../icons/emblems';
import { installPressTracking } from './press';
import { applyPaintedFrames, loadArtManifest } from '../art/ArtImage';

export interface KitStatus { fonts: 'loaded' | 'timeout' | 'error' | 'pending'; fontsMs: number; bake: BakeResult | null; manifest: boolean }

const status: KitStatus = { fonts: 'pending', fontsMs: 0, bake: null, manifest: false };
let ready: Promise<KitStatus> | null = null;

/** The four faces boot awaits (ui-ux §18.2); 700 and italic load on first use. */
const BOOT_FACES = ['400 16px Alegreya', '500 16px "Alegreya SC"', '700 16px "Alegreya SC"', '400 16px "Ruslan Display"'];

export function loadFonts(timeoutMs = 1500): Promise<KitStatus['fonts']> {
  if (typeof document === 'undefined' || !document.fonts) return Promise.resolve('error');
  const t0 = performance.now();
  const all = Promise.all(BOOT_FACES.map((f) => document.fonts.load(f, 'АаЖжЁё'))).then(() => 'loaded' as const, () => 'error' as const);
  const timeout = new Promise<'timeout'>((res) => setTimeout(() => res('timeout'), timeoutMs));
  return Promise.race([all, timeout]).then((r) => { status.fonts = r; status.fontsMs = Math.round(performance.now() - t0); return r; });
}

/** The string-measure pass (ui-ux §3.2): the seal cell is content-sized from «Ристалище» at the tab font. */
export function measureLabels(root: HTMLElement = document.documentElement, ristalishche = 'Ристалище'): void {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return;
  const cs = getComputedStyle(root);
  const scale = Math.min(Number.parseFloat(cs.getPropertyValue('--text-scale')) || 1, 1.3);
  const px = (matchMedia('(min-width: 1024px) and (min-height: 521px)').matches ? 14 : 13) * scale;
  ctx.font = `700 ${px}px "Alegreya SC", Georgia, serif`;
  root.style.setProperty('--label-w-ristalishche', `${Math.ceil(ctx.measureText(ristalishche).width + px * 0.02 * ristalishche.length)}px`);
}

/** Installs the kit once; resolves when fonts settled (or timed out) and the bake finished. */
export function installKit(opts: { root?: HTMLElement; seed?: string } = {}): Promise<KitStatus> {
  if (ready) return ready;
  const root = opts.root ?? document.documentElement;
  // Shipping art is generated from meshes, seeded materials and ink glyphs.
  root.dataset.artSource = 'procedural';
  applyTokens(root);
  applyOrnaments(root);
  ensureShellSprite();
  ensureEmblemSprite();
  installPressTracking();
  if (!root.dataset.motion && matchMedia('(prefers-reduced-motion: reduce)').matches) root.dataset.motion = 'reduced';
  const bake = bakeMaterials(opts.seed, root).then((b) => { status.bake = b; }, (e) => console.warn('[ui] material bake failed', e));
  const manifest = loadArtManifest().then((ok) => { status.manifest = ok; if (root.dataset.artSource !== 'procedural' && ok) applyPaintedFrames(root); });
  const fonts = loadFonts().then(() => measureLabels(root));
  ready = Promise.all([bake, manifest, fonts]).then(() => { root.dataset.kit = 'ready'; return status; });
  return ready;
}

export function kitStatus(): KitStatus { return status; }
